/**
 * Clipchamp-style rails: media, record, library, text, brand, templates,
 * story on the left; captions and transitions on the right.
 */

import { useEffect, useRef, useState, type DragEvent } from 'react';
import type { SceneTransition, VideoBrand } from '@canvas/shared-types';
import { EDITOR_TEMPLATES } from '@/data/editorTemplates';
import { TEXT_STYLE_CARDS, type TextStyleCard } from '@/data/textStyles';
import { TRANSITIONS, transitionDef } from '@/data/transitions';
import { VIDEO_SIZE_PRESETS, type VideoSizePreset } from '@/data/videoExportPresets';
import { listenForCaptions, micCaptionSupported } from '@/lib/video/captionProvider';
import { applyClipTransition, isClipProject, transitionBlockReason, transitionHoldMs, withBrand } from '@/lib/video/timelineEdit';
import { useVideoStore } from '@/stores/videoStore';
import ContentPanel from './ContentPanel';
import LibraryBrowser from './LibraryBrowser';
import RecordStudio, { type RecordMode } from './RecordStudio';
import type { ContentPanelState } from '@/lib/reel/contentPanelState';

export type LeftRail = 'media' | 'record' | 'library' | 'text' | 'brand' | 'templates' | 'story';
export type RightRail = 'captions' | 'transitions' | null;

interface StudioPanelsProps {
  left: LeftRail;
  right: RightRail;
  contentState: ContentPanelState;
  onIdea: (idea: string) => void;
  onLine: (index: number, line: string) => void;
  onWrite: () => void;
  onMakePictures: () => void;
  onUsePhotos: () => void;
  onToggleAdjust: () => void;
  onMovePictures: (move: boolean) => void;
  onTemplate: (id: string) => void;
  onGradient: (id: string) => void;
  onTone: () => void;
  onLibrary: (id: string) => void;
  onText: (lookId: string, sample?: string) => void;
  onCaption: (text: string, source: 'manual' | 'auto', atMs?: number) => void;
  onImport: (file: File) => void;
  onRecorded: (file: File, kind: 'video' | 'audio', atMs: number, durationMs?: number) => void | Promise<void>;
  busy: boolean;
  note: string | null;
}

function drag(event: DragEvent, payload: Record<string, string>): void {
  const body = JSON.stringify(payload);
  event.dataTransfer.setData('application/x-craftara-clip', body);
  event.dataTransfer.setData('text/plain', body);
  event.dataTransfer.effectAllowed = 'copy';
}

export function LeftBrowser(props: StudioPanelsProps): JSX.Element {
  const video = useVideoStore((s) => s.video);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>('YouTube');
  const templates = EDITOR_TEMPLATES.filter((template) => {
    const hay = `${template.title} ${template.blurb} ${template.category}`.toLowerCase();
    const matchesQuery = hay.includes(query.trim().toLowerCase());
    const matchesCategory = !category || template.category === category;
    return matchesQuery && matchesCategory;
  });

  return (
    <div className="vt-browser">
      {props.left === 'templates' ? (
        <>
          <h2>Templates</h2>
          <input
            className="vt-browser-search"
            placeholder="Search templates"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {category ? (
            <button type="button" className="vt-btn" onClick={() => setCategory(null)}>
              ← {category}
            </button>
          ) : (
            <button type="button" className="vt-btn" onClick={() => setCategory('YouTube')}>
              YouTube
            </button>
          )}
          <div className="vt-card-grid">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                className="vt-template-card"
                draggable
                onDragStart={(event) => drag(event, { action: 'template', id: template.id })}
                onClick={() => props.onTemplate(template.id)}
              >
                <span className="vt-template-swatch" style={{ background: template.swatch }} />
                <strong>{template.title}</strong>
                <span>{template.blurb}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {props.left === 'media' ? (
        <>
          <h2>My media</h2>
          <label className="vt-btn vt-btn-primary">
            Import
            <input
              type="file"
              accept="video/*,image/*,audio/*"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) props.onImport(file);
              }}
            />
          </label>
          <MediaGrid />
          {props.note ? <p className="vt-notice">{props.note}</p> : null}
        </>
      ) : null}

      {props.left === 'library' ? (
        <LibraryBrowser onPlace={props.onLibrary} onGradient={props.onGradient} onTone={props.onTone} />
      ) : null}

      {props.left === 'text' ? (
        <TextMenu onAdd={(id, sample) => props.onText(id, sample)} />
      ) : null}

      {props.left === 'brand' && isClipProject(video) ? (
        <BrandEditor brand={video.brand} />
      ) : null}

      {props.left === 'record' ? (
        <RecordPanel busy={props.busy} note={props.note} onRecorded={props.onRecorded} />
      ) : null}

      {props.left === 'story' ? (
        <ContentPanel
          state={props.contentState}
          onIdea={props.onIdea}
          onLine={props.onLine}
          onWrite={props.onWrite}
          onMakePictures={props.onMakePictures}
          onUsePhotos={props.onUsePhotos}
          onToggleAdjust={props.onToggleAdjust}
          onMovePictures={props.onMovePictures}
        />
      ) : null}
    </div>
  );
}

function TextMenu({ onAdd }: { onAdd: (lookId: string, sample: string) => void }): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const styles = TEXT_STYLE_CARDS.filter((card) => card.group === 'style' && (showAll || !card.extra));
  const titles = TEXT_STYLE_CARDS.filter((card) => card.group === 'title');
  const plain = TEXT_STYLE_CARDS.find((card) => card.group === 'plain');
  const add = (card: TextStyleCard): void => onAdd(card.id, card.sample);
  return (
    <>
      <h2>Text</h2>
      {plain ? (
        <section className="vt-text-block">
          <h3>Plain text</h3>
          <button
            type="button"
            className="vt-text-plain"
            draggable
            onDragStart={(event) => drag(event, { action: 'text', id: plain.id, text: plain.sample })}
            onClick={() => add(plain)}
          >
            <span>Text</span>
          </button>
        </section>
      ) : null}
      <section className="vt-text-block">
        <div className="vt-text-head">
          <h3>Text styles</h3>
          <button type="button" className="vt-text-see" onClick={() => setShowAll((open) => !open)}>
            {showAll ? 'Show less' : 'See all'}
          </button>
        </div>
        <div className="vt-text-grid">
          {styles.map((card) => (
            <button
              key={card.id}
              type="button"
              className="vt-text-card"
              draggable
              onDragStart={(event) => drag(event, { action: 'text', id: card.id, text: card.sample })}
              onClick={() => add(card)}
            >
              <span className={`vt-look vt-look-${card.id}`}>{card.sample}</span>
            </button>
          ))}
        </div>
      </section>
      <section className="vt-text-block">
        <h3>Title</h3>
        <div className="vt-text-grid">
          {titles.map((card) => (
            <button
              key={card.id}
              type="button"
              className={`vt-text-card ${card.id === 'typewriter' ? 'dark' : ''}`}
              draggable
              onDragStart={(event) => drag(event, { action: 'text', id: card.id, text: card.sample })}
              onClick={() => add(card)}
            >
              <span className={`vt-look vt-look-${card.id}`}>{card.id === 'typewriter' ? 'Typewriter' : card.sample}</span>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}

function MediaKindIcon({ kind }: { kind: 'image' | 'video' | 'audio' }): JSX.Element {
  const common = {
    viewBox: '0 0 24 24',
    width: 14,
    height: 14,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
  };
  if (kind === 'video') {
    return (
      <svg {...common}>
        <rect x="3" y="6" width="12" height="12" rx="2" />
        <path d="M15 10l6-3v10l-6-3z" />
      </svg>
    );
  }
  if (kind === 'audio') {
    return (
      <svg {...common}>
        <path d="M9 18V7l10-2v11" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="16" cy="16" r="3" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.4" />
      <path d="M4 16l4-3 3 2 3-4 6 5" />
    </svg>
  );
}

function MediaGrid(): JSX.Element {
  const clips = useVideoStore((s) => (isClipProject(s.video) ? s.video.clips : []));
  const media = clips.filter(
    (clip): clip is typeof clip & { kind: 'video' | 'image' | 'audio' } =>
      clip.kind === 'video' || clip.kind === 'image' || clip.kind === 'audio'
  );
  if (!media.length) return <p className="vt-browser-empty">Import a video, photo, or audio file.</p>;
  return (
    <div className="vt-media-list">
      {media.map((clip) => (
        <button
          key={clip.id}
          type="button"
          className="vt-media-row"
          title={clip.name}
          aria-label={`${clip.name}, ${clip.kind}`}
          draggable
          onDragStart={(event) => drag(event, { action: 'duplicate', id: clip.id })}
          onClick={() => useVideoStore.getState().selectClip(clip.id)}
        >
          <span className={`vt-media-kind vt-media-kind-${clip.kind}`}>
            <MediaKindIcon kind={clip.kind} />
          </span>
          <strong>{clip.name}</strong>
        </button>
      ))}
    </div>
  );
}

function BrandEditor({ brand }: { brand: VideoBrand }): JSX.Element {
  const [name, setName] = useState(brand.name);
  return (
    <>
      <h2>Brand kit</h2>
      <label className="vt-field">
        Name
        <input value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      {brand.colors.map((color) => (
        <label key={color.id} className="vt-field">
          {color.name}
          <input
            type="color"
            value={color.hex}
            onChange={(event) => {
              const colors = brand.colors.map((item) =>
                item.id === color.id ? { ...item, hex: event.target.value } : item
              );
              useVideoStore.getState().changeTimeline((video) => withBrand(video, { ...brand, name, colors }));
            }}
          />
        </label>
      ))}
      {brand.fonts.map((font) => (
        <p key={font.id} className="vt-browser-empty">
          {font.name}: {font.family}
        </p>
      ))}
      <button
        type="button"
        className="vt-btn"
        onClick={() => {
          const url = brand.logoUrl;
          if (!url) return;
          useVideoStore.getState().changeTimeline((video) => {
            if (!isClipProject(video)) return video;
            return {
              ...withBrand(video, { ...brand, name }),
            };
          });
          window.dispatchEvent(new CustomEvent('craftara:add-logo'));
        }}
      >
        Drop logo on timeline
      </button>
    </>
  );
}

function RecordPanel({
  busy,
  note,
  onRecorded,
}: {
  busy: boolean;
  note: string | null;
  onRecorded: (file: File, kind: 'video' | 'audio', atMs: number, durationMs?: number) => void | Promise<void>;
}): JSX.Element {
  const [studio, setStudio] = useState<RecordMode | null>(null);
  return (
    <>
      <h2>Record & create</h2>
      <p className="vt-browser-empty">Open the studio, record a take, then add it to the timeline.</p>
      <button type="button" className="vt-btn" disabled={busy} onClick={() => setStudio('camera')}>
        Camera
      </button>
      <button type="button" className="vt-btn" disabled={busy} onClick={() => setStudio('screen')}>
        Screen
      </button>
      <button type="button" className="vt-btn" disabled={busy} onClick={() => setStudio('voice')}>
        Voiceover
      </button>
      {note ? <p className="vt-notice">{note}</p> : null}
      {studio ? (
        <RecordStudio
          mode={studio}
          onClose={() => setStudio(null)}
          onRecorded={(file, kind, atMs, durationMs) => onRecorded(file, kind, atMs, durationMs)}
        />
      ) : null}
    </>
  );
}

export function RightBrowser({
  right,
  onCaption,
}: {
  right: RightRail;
  onCaption: (text: string, source: 'manual' | 'auto', atMs?: number) => void;
}): JSX.Element | null {
  const video = useVideoStore((s) => s.video);
  const selectedId = useVideoStore((s) => s.selectedClipId);
  const [line, setLine] = useState('Caption');
  const [listening, setListening] = useState(false);
  const stopRef = useRef<{ stop: () => void } | null>(null);

  useEffect(() => () => stopRef.current?.stop(), []);

  if (!right || !isClipProject(video)) return null;
  const selected = video.clips.find((clip) => clip.id === selectedId);

  return (
    <div className="vt-browser vt-browser-right">
      {right === 'captions' ? (
        <>
          <h2>Captions</h2>
          <input className="vt-browser-search" value={line} onChange={(event) => setLine(event.target.value)} />
          <button type="button" className="vt-btn vt-btn-primary" onClick={() => onCaption(line, 'manual')}>
            Add caption
          </button>
          <button
            type="button"
            className="vt-btn"
            disabled={!micCaptionSupported()}
            onClick={() => {
              if (listening) {
                stopRef.current?.stop();
                stopRef.current = null;
                setListening(false);
                useVideoStore.getState().pause();
                return;
              }
              const handle = listenForCaptions((text) => {
                onCaption(text, 'auto', useVideoStore.getState().currentTimeMs);
              });
              if (!handle) return;
              stopRef.current = handle;
              setListening(true);
              useVideoStore.getState().play();
            }}
          >
            {listening ? 'Stop auto captions' : 'Auto captions (mic)'}
          </button>
          {!micCaptionSupported() ? <p className="vt-browser-empty">This browser has no microphone speech recognition.</p> : null}
        </>
      ) : (
        <>
          <h2>Transition</h2>
          <p className="vt-browser-empty">Drag a tile onto a clip, or select a clip that follows another on the same track.</p>
          <div className="vt-trans-grid">
            {TRANSITIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`vt-trans-card ${selected?.transitionIn?.id === item.id ? 'active' : ''}`}
                title={item.hint}
                aria-label={`${item.label}. ${item.hint}`}
                draggable
                onDragStart={(event) => drag(event, { action: 'transition', id: item.id })}
                onClick={() => {
                  const store = useVideoStore.getState();
                  const clipId = store.selectedClipId;
                  if (!clipId || !store.video) {
                    store.setNotice('Select a clip, then choose a transition.');
                    return;
                  }
                  const blocked = transitionBlockReason(store.video, clipId);
                  if (item.id !== 'cut' && item.id !== 'none' && blocked) {
                    store.setNotice(blocked);
                    return;
                  }
                  const def = transitionDef(item.id);
                  const transition: SceneTransition = {
                    id: item.id,
                    durationMs: def?.defaultDurationMs ?? 500,
                  };
                  store.pause();
                  store.changeTimeline((doc) => applyClipTransition(doc, clipId, transition));
                  const hold = store.video ? transitionHoldMs(store.video, clipId) : null;
                  if (hold != null) store.seek(hold);
                  store.setNotice(null);
                }}
              >
                <span className={`vt-trans-preview vt-trans-${item.id}`} aria-hidden="true">
                  <i />
                  <i />
                </span>
                <strong>{item.label}</strong>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function SizeMenu({ onPick }: { onPick: (preset: VideoSizePreset) => void }): JSX.Element {
  return (
    <label className="vt-size">
      Size
      <select
        aria-label="Frame size"
        defaultValue=""
        onChange={(event) => {
          const preset = VIDEO_SIZE_PRESETS.find((item) => item.id === event.target.value);
          event.target.value = '';
          if (preset) onPick(preset);
        }}
      >
        <option value="">Size</option>
        {VIDEO_SIZE_PRESETS.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {preset.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ShareDialog({
  open,
  onClose,
  onDownload,
}: {
  open: boolean;
  onClose: () => void;
  onDownload: () => void;
}): JSX.Element | null {
  const [copied, setCopied] = useState(false);
  if (!open) return null;
  return (
    <div className="vt-share-backdrop" onClick={onClose}>
      <div className="vt-share" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Share">
        <h2>Share</h2>
        <p>Copy a link to this project, or download the video.</p>
        <button
          type="button"
          className="vt-btn"
          onClick={() => {
            void navigator.clipboard.writeText(window.location.href).then(() => setCopied(true));
          }}
        >
          {copied ? 'Link copied' : 'Copy link'}
        </button>
        <button type="button" className="vt-btn vt-btn-primary" onClick={onDownload}>
          Download
        </button>
        <button type="button" className="vt-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

